import type { CalculableSubItem, Rates } from './breakdownCalculator';
import { getSubItemValue } from './breakdownCalculator';
import type { SubItemCategory } from '../types/subItemCategory';
import { isQtyRateCategory, BUILT_IN_KEYS } from '../types/subItemCategory';
import type { SubItemTypeMetaMap } from '../types/subItemTypeMeta';
import { getUnitLabel } from './unitMeta';

export interface InvoiceDisplayItem {
  type: string;
  displayQty: number | null;
  displayRate: number | null;
  displayAmount: number;
  displayUnit: string | null;
  note: string | null;
  original: CalculableSubItem;
}

function isItemTaxable(item: CalculableSubItem, categories: SubItemCategory[]): boolean {
  const cat = categories.find((c) => c.key === item.type);
  return cat ? cat.taxApplicable : false;
}

function isItemMarkupable(item: CalculableSubItem, categories: SubItemCategory[]): boolean {
  // An explicit per-item rate outranks the category flag, which only sets the
  // default. Mirrors calculateMarkup — the two must agree or the invoice drifts
  // from the breakdown total.
  if (item.markupRate != null) return true;
  const cat = categories.find((c) => c.key === item.type);
  // Carpet catalog items (no match in sub_item_categories) default to
  // markupApplicable: false — catalog prices are already customer-facing.
  return cat ? cat.markupApplicable : false;
}

function getItemDisplayFactors(
  item: CalculableSubItem,
  categories: SubItemCategory[],
  rates: Rates,
): { taxFactor: number; markupFactor: number } {
  const markupRate = item.markupRate != null ? Number(item.markupRate) : rates.markupRate;
  return {
    taxFactor: isItemTaxable(item, categories) ? (1 + rates.taxRate) : 1,
    markupFactor: isItemMarkupable(item, categories) ? (1 + markupRate) : 1,
  };
}

/**
 * Transforms internal sub-items into client-facing display values.
 * - Labor: rate includes tax + markup
 * - Non-labor: amount includes markup + proportional profit share
 * - Discount: returned as a negative displayAmount so summation subtracts it
 * - Profit: distributed into pool items (non-labor preferred, labor fallback);
 *   emitted as its own row only when no distributable items exist.
 */
export function getInvoiceDisplayItems(
  subItems: CalculableSubItem[],
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): InvoiceDisplayItem[] {
  // Resolve the per-line unit label for qty×rate display (e.g. "sq ft").
  // The label lives directly on the sub-item (`unitKey`); amount-mode items pass null.
  const resolveUnit = (item: CalculableSubItem): string =>
    getUnitLabel({
      unitKey: item.unitKey ?? null,
      categoryUnitLabel: typeMeta[item.type]?.unitLabel ?? null,
      rateType: (item.rateType as 'hourly' | 'daily' | null | undefined) ?? null,
    });

  // Calculate total profit to distribute
  const totalProfit = subItems
    .filter(item => item.type === BUILT_IN_KEYS.PROFIT)
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  // Compute the distribution pool: prefer non-labor items, fall back to all
  // distributable items (i.e. labor) when no non-labor items exist.
  const distributable = subItems.filter(item =>
    item.type !== BUILT_IN_KEYS.PROFIT &&
    item.type !== BUILT_IN_KEYS.DISCOUNT
  );
  const nonLabor = distributable.filter(item => item.type !== BUILT_IN_KEYS.LABOR);
  const distributionPool = nonLabor.length > 0 ? nonLabor : distributable;

  const poolPostValues = distributionPool.map(item => {
    const baseValue = getSubItemValue(item, typeMeta);
    const { taxFactor, markupFactor } = getItemDisplayFactors(item, categories, rates);
    return baseValue * taxFactor * markupFactor;
  });
  const totalPoolPostValue = poolPostValues.reduce((sum, v) => sum + v, 0);

  const results: InvoiceDisplayItem[] = [];

  for (const item of subItems) {
    // Skip profit — it's distributed into other items
    if (item.type === BUILT_IN_KEYS.PROFIT) continue;

    const isLabor = item.type === BUILT_IN_KEYS.LABOR;
    const isDiscount = item.type === BUILT_IN_KEYS.DISCOUNT;
    const isQtyRate = isQtyRateCategory(item.type, typeMeta);

    if (isDiscount) {
      results.push({
        type: item.type,
        displayQty: isQtyRate ? (Number(item.hours) || 0) : null,
        displayRate: null,
        displayAmount: -(Number(item.amount) || 0),
        displayUnit: isQtyRate ? resolveUnit(item) : null,
        note: item.note ?? null,
        original: item,
      });
      continue;
    }

    if (isLabor) {
      const hours = Number(item.hours) || 0;
      const rate = Number(item.rate) || 0;
      const { taxFactor, markupFactor } = getItemDisplayFactors(item, categories, rates);

      const idx = distributionPool.indexOf(item);
      const itemPostValue = poolPostValues[idx] ?? 0;
      const profitShare = totalPoolPostValue > 0 && totalProfit > 0
        ? totalProfit * (itemPostValue / totalPoolPostValue)
        : 0;

      const baseDisplayRate = rate * taxFactor * markupFactor;
      const displayRate = hours > 0 ? baseDisplayRate + (profitShare / hours) : baseDisplayRate;
      results.push({
        type: item.type,
        displayQty: hours,
        displayRate,
        displayAmount: hours * displayRate,
        displayUnit: resolveUnit(item),
        note: item.note ?? null,
        original: item,
      });
      continue;
    }

    // Non-labor, non-discount, non-profit
    const idx = distributionPool.indexOf(item);
    const itemPostValue = poolPostValues[idx] ?? 0;
    const profitShare = totalPoolPostValue > 0 && totalProfit > 0
      ? totalProfit * (itemPostValue / totalPoolPostValue)
      : 0;

    if (isQtyRate) {
      const hours = Number(item.hours) || 0;
      const rate = Number(item.rate) || 0;
      const { taxFactor, markupFactor } = getItemDisplayFactors(item, categories, rates);
      const baseDisplayRate = rate * taxFactor * markupFactor;
      const displayRate = hours > 0 ? baseDisplayRate + (profitShare / hours) : baseDisplayRate;

      results.push({
        type: item.type,
        displayQty: hours,
        displayRate,
        displayAmount: hours * displayRate,
        displayUnit: resolveUnit(item),
        note: item.note ?? null,
        original: item,
      });
    } else {
      // amount-mode: displayQty and displayRate are null
      const amount = Number(item.amount) || 0;
      const { taxFactor, markupFactor } = getItemDisplayFactors(item, categories, rates);
      const displayAmount = amount * taxFactor * markupFactor + profitShare;
      results.push({
        type: item.type,
        displayQty: null,
        displayRate: null,
        displayAmount,
        displayUnit: null,
        note: item.note ?? null,
        original: item,
      });
    }
  }

  if (distributionPool.length === 0 && totalProfit > 0) {
    results.push({
      type: BUILT_IN_KEYS.PROFIT,
      displayQty: null,
      displayRate: null,
      displayAmount: totalProfit,
      displayUnit: null,
      note: null,
      // Safe: this block only runs when totalProfit > 0, so a profit sub-item exists in subItems.
      original: subItems.find(s => s.type === BUILT_IN_KEYS.PROFIT)!,
    });
  }

  return results;
}

/**
 * Calculate the total invoice amount for a set of line items.
 * This is the grand total shown on a final invoice (before deducting payments).
 */
export function calculateInvoiceTotal(
  lineItems: Array<{ subItems: CalculableSubItem[] }>,
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
  roundingPrecision?: number | null,
): number {
  let total = 0;
  for (const li of lineItems) {
    const displayItems = getInvoiceDisplayItems(li.subItems, rates, typeMeta, categories);
    total += displayItems.reduce((sum, item) => sum + item.displayAmount, 0);
  }

  if (roundingPrecision && roundingPrecision > 0) {
    const rounded = Math.round(total / roundingPrecision) * roundingPrecision;
    if (Math.abs(rounded - total) >= 0.005) {
      total = rounded;
    }
  }

  return total;
}
